/**
 * Компиляция пользовательских паттернов (regex-источник) в JS RegExp.
 *
 * Паттерны пишут люди в настройках и частично перенесены из Python-прототипа,
 * поэтому компилятор сглаживает различия:
 *  - `\b`, `\B`, `\w`, `\W` в JS понимают только латиницу — переводим в Unicode-аналоги,
 *    иначе `\bнал\b` никогда не сработает;
 *  - `ё` приравнивается к `е` (и в паттерне, и в тексте);
 *  - питоновские `(?P<name>` и ведущий `(?i)` приводятся к JS-синтаксису;
 *  - «лишние» экранирования (`\-` вне класса) не ломают компиляцию в режиме `u`.
 */

const WORD = '\\p{L}\\p{N}_';
const BOUNDARY = `(?:(?<=[${WORD}])(?![${WORD}])|(?<![${WORD}])(?=[${WORD}]))`;
const NOT_BOUNDARY = `(?:(?<=[${WORD}])(?=[${WORD}])|(?<![${WORD}])(?![${WORD}]))`;
const SYNTAX_CHARS = new Set(['^', '$', '\\', '.', '*', '+', '?', '(', ')', '[', ']', '{', '}', '|', '/']);

export function normalizeText(text: string): string {
  return text.replace(/ё/g, 'е').replace(/Ё/g, 'Е');
}

/**
 * Текст сообщения перед проверкой паттернов: ё → е, переносы строк → пробелы.
 * Клиенты пишут запрос в несколько строк («Мой тез / Ваш синий»), а `.` в regex перенос не захватывает.
 * Каждый перенос заменяется ровно одним пробелом — позиции совпадений остаются те же (для подсветки).
 */
export function normalizeMessage(text: string): string {
  return normalizeText(text).replace(/[\r\n]/g, ' ');
}

export function translatePattern(source: string): string {
  let src = normalizeText(source.trim());
  src = src.replace(/^\(\?i\)/, '').replace(/\(\?P</g, '(?<');

  let out = '';
  let inClass = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (ch === '\\' && i + 1 < src.length) {
      const next = src[++i]!;
      if (!inClass) {
        if (next === 'b') { out += BOUNDARY; continue; }
        if (next === 'B') { out += NOT_BOUNDARY; continue; }
        if (next === 'w') { out += `[${WORD}]`; continue; }
        if (next === 'W') { out += `[^${WORD}]`; continue; }
        if (!/[\p{L}\p{N}]/u.test(next) && !SYNTAX_CHARS.has(next)) { out += next; continue; }
      } else if (next === 'w') {
        out += WORD;
        continue;
      }
      out += '\\' + next;
      continue;
    }
    if (ch === '[' && !inClass) inClass = true;
    else if (ch === ']' && inClass) inClass = false;
    out += ch;
  }
  return out;
}

const cache = new Map<string, RegExp>();

/** Компилирует паттерн; бросает SyntaxError при невалидном regex. */
export function compilePattern(source: string): RegExp {
  let re = cache.get(source);
  if (!re) {
    re = new RegExp(translatePattern(source), 'iu');
    if (cache.size > 5000) cache.clear();
    cache.set(source, re);
  }
  return re;
}

/** Возвращает текст ошибки или null, если паттерн валиден. */
export function validatePattern(source: string): string | null {
  if (!source.trim()) return 'Пустой паттерн';
  try {
    compilePattern(source);
    return null;
  } catch (e) {
    return e instanceof Error ? e.message : String(e);
  }
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
}

/**
 * Список слов/фраз → один паттерн «целое слово из списка».
 * `*` в конце слова — любое окончание (`газу*` → газуй, газуйте); пробелы во фразе — любые пробелы.
 */
export function keywordsToPattern(keywords: readonly string[]): string | null {
  const words = [...new Set(keywords.map((k) => normalizeText(k.trim().toLowerCase())).filter((k) => k.replace(/\*/g, '')))];
  if (!words.length) return null;
  words.sort((a, b) => b.length - a.length);
  const toRe = (w: string) => {
    const prefix = w.endsWith('*');
    const body = w.replace(/\*+$/, '').split(/\s+/).map(escapeRegex).join('\\s+');
    return prefix ? `${body}[${WORD}]*` : body;
  };
  return `(?<![${WORD}])(?:${words.map(toRe).join('|')})(?![${WORD}])`;
}

const keywordPatternCache = new WeakMap<readonly string[], string | null>();

/** Первое совпадение из словаря (или null). Паттерн кэшируется на сам массив — он меняется только при сохранении настроек. */
export function findKeywordHit(keywords: readonly string[], text: string): PatternHit | null {
  if (!keywords.length) return null;
  let p = keywordPatternCache.get(keywords);
  if (p === undefined) {
    p = keywordsToPattern(keywords);
    keywordPatternCache.set(keywords, p);
  }
  return p ? findHit([p], text) : null;
}

export interface PatternHit {
  pattern: string;
  match: string;
}

/** Первый сработавший паттерн из списка (для объяснимости решений). */
export function findHit(patterns: readonly string[], text: string): PatternHit | null {
  const normalized = normalizeMessage(text);
  for (const pattern of patterns) {
    let re: RegExp;
    try {
      re = compilePattern(pattern);
    } catch {
      continue; // невалидные паттерны отсекаются валидацией настроек; здесь просто пропускаем
    }
    const m = re.exec(normalized);
    if (m) return { pattern, match: m[0] };
  }
  return null;
}
